import { DealerSpaceParameterKey } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import {
  SPACE_DATE_RANGE_PANEL_VALUES,
  defineSpaceParameterPanelValues,
  firstSelectionToPanelValue,
  useSpaceParameterizedFilter,
} from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartyFilter } from '@src/hooks/filters/useCounterpartyFilter';
import { useIDFilter } from '@src/hooks/filters/useIDFilter';
import { useSidesFilter } from '@src/hooks/filters/useSidesFilter';
import { useSubAccountsFilter } from '@src/hooks/filters/useSubAccountsFilter';
import { isExcludedByResolvedUsers, useResolvedUsers, useUsersFilter } from '@src/hooks/filters/useUsersFilter';
import { useCustomerUsers } from '@src/hooks/useCustomer';
import { useUser } from '@src/hooks/useUser';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import {
  EMPTY_OBJECT,
  FilterClauseType,
  IconName,
  PricingModeEnum,
  TradeStatusEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  isExcludedByCurrencyOrBasePairFilters,
  removeEmptyFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useDateRangeFilter,
  useSecuritiesContext,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type CustomerTrade,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { isEqual, keys, startCase, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useMarketAccountsFilter } from '../../../hooks/filters/useMarketAccountsFilter';

export interface CustomerTradeFilter extends DateRangeFilter {
  Sides?: string[];
  Counterparty?: string[];
  MarketAccounts?: string[];
  CustomerUsers?: string[];
  Statuses?: string[];
  SubAccounts?: string[];
  Symbols?: string[];
  Users?: string[];
  HideApiCalls?: boolean;
  OrderID?: string;
  RFQID?: string;
  TradeID?: string;
  PricingMode?: string[];
  Currencies?: string[];
  BasePairs?: string[];
}
export interface UseCustomerTradeFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.BasePairs]: 'BasePairs',
  [DealerSpaceParameterKey.Counterparty]: 'Counterparty',
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
  [DealerSpaceParameterKey.DateRange]: SPACE_DATE_RANGE_PANEL_VALUES,
  [DealerSpaceParameterKey.MarketAccounts]: 'MarketAccounts',
  [DealerSpaceParameterKey.OrderID]: firstSelectionToPanelValue('OrderID'),
  [DealerSpaceParameterKey.PricingMode]: 'PricingMode',
  [DealerSpaceParameterKey.RFQID]: firstSelectionToPanelValue('RFQID'),
  [DealerSpaceParameterKey.Sides]: 'Sides',
  [DealerSpaceParameterKey.SubAccounts]: 'SubAccounts',
  [DealerSpaceParameterKey.Symbols]: 'Symbols',
  [DealerSpaceParameterKey.Users]: 'Users',
});
export const CUSTOMER_TRADE_SPACE_PARAMETER_KEYS = CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT.keys;

export function useCustomerTradeFilter({ persistedBlotterTable }: UseCustomerTradeFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const { Name: currentUserName } = useUser();
  const [initialFilter] = useState(() => cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter));
  const [localFilter, setFilter] = useState<CustomerTradeFilter>((initialFilter as CustomerTradeFilter) || {});
  const { subAccountsEnabled } = useSubAccounts();
  const { securitiesList, securitiesBySymbol } = useSecuritiesContext();

  const changeLocalFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = localFilter;
      const newFilter = action instanceof Function ? action(localFilter) : action;
      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [localFilter, saveFilter]
  );
  const { changeFilter, controlledKeys, filter, getSelfManagedFilterBuilderProps } = useSpaceParameterizedFilter({
    filter: localFilter,
    changeFilter: changeLocalFilter,
    spaceParameterPanelValues: CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT,
  });
  const isDateRangeControlled = controlledKeys.includes(DealerSpaceParameterKey.DateRange);

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<CustomerTrade>>(
    row => {
      const data = row.data;

      if (filterExistsAndExcludes(filter, 'MarketAccounts', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Sides', data, 'Side')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'TradeStatus')) {
        return false;
      }
      if (isExcludedByResolvedUsers(resolvedUsers, data?.User)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'CustomerUsers', data, 'CustomerUser')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'PricingMode', data, 'PricingMode')) {
        return false;
      }
      if (
        isExcludedByCurrencyOrBasePairFilters(data?.Symbol, securitiesBySymbol, filter.Currencies, filter.BasePairs)
      ) {
        return false;
      }
      if (
        filter.SubAccounts &&
        filter.SubAccounts.length > 0 &&
        !(data?.SubAccount && filter.SubAccounts.includes(data.SubAccount))
      ) {
        return false;
      }
      return true;
    },
    [filter, resolvedUsers, securitiesBySymbol]
  );

  const subAccountsFilter = useSubAccountsFilter(EMPTY_OBJECT);
  const usersFilter = useUsersFilter();
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer' });
  const customerUsers = useCustomerUsers();
  const marketAccountsFilter = useMarketAccountsFilter({ label: 'Customer Market Account' });
  const sidesFilter = useSidesFilter();
  const idFilter = useIDFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () =>
      [
        counterpartyFilter,
        marketAccountsFilter,
        sidesFilter,
        {
          key: 'Statuses',
          label: 'Status',
          icon: IconName.CheckCircle,
          options: values(TradeStatusEnum),
          getOptionLabel: (option: string) => option,
        },
        subAccountsEnabled ? subAccountsFilter : undefined,
        {
          key: 'Symbols',
          label: 'Symbol',
          icon: IconName.CurrencyDollar,
          options: securitiesList.map(sec => sec.Symbol),
          getOptionLabel: (option: string) => securitiesBySymbol.get(option)?.DisplaySymbol || option,
        },
        currencyOfSymbolFilter,
        basePairFilter,
        usersFilter,
        {
          key: 'CustomerUsers',
          label: 'Customer User',
          icon: IconName.User,
          options: customerUsers?.map(c => c.Email) ?? [],
          getOptionLabel: (option: string) => option,
          clearUnsupportedSelectionsOnInit: false,
        },
        {
          key: 'PricingMode',
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: startCase,
        },
        { ...idFilter, key: 'OrderID', label: 'Order ID' },
        { ...idFilter, key: 'RFQID', label: 'RFQ ID' },
        { ...idFilter, key: 'TradeID', label: 'Trade ID' },
      ].compact(),
    [
      securitiesBySymbol,
      securitiesList,
      currencyOfSymbolFilter,
      basePairFilter,
      subAccountsEnabled,
      subAccountsFilter,
      usersFilter,
      sidesFilter,
      idFilter,
      customerUsers,
      counterpartyFilter,
      marketAccountsFilter,
    ]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof CustomerTradeFilter)[]).forEach((key: keyof CustomerTradeFilter) => {
        switch (key) {
          case '_start':
          case 'StartDate':
          case 'EndDate':
          case 'Statuses':
            return;
          default:
            clauses.push({
              key: key,
              type: FilterClauseType.INCLUSIVE,
              selections: filter[key] as string[],
            });
        }
      });
    }
    return clauses;
  }, [filter]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: CustomerTradeFilter = removeEmptyFilters<CustomerTradeFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as CustomerTradeFilter),
          OrderID: filterClausesByPropertyKey.get('OrderID')?.selections?.[0],
          RFQID: filterClausesByPropertyKey.get('RFQID')?.selections?.[0],
          TradeID: filterClausesByPropertyKey.get('TradeID')?.selections?.[0],
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () =>
      getSelfManagedFilterBuilderProps({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }),
    [getSelfManagedFilterBuilderProps, filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;
  const blotterTableFilterProps = useMemo(
    () => (isDateRangeControlled ? { showDateRangePicker: false } : dateRangeFilter),
    [dateRangeFilter, isDateRangeControlled]
  ) satisfies Partial<BlotterTableFiltersProps>;
  return {
    initialFilter,
    filter,
    clientSideFilter,
    changeFilter,
    contextMenuFilterableProperties: filterableProperties,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
    // shortcut to spread props into the BlotterTableFilters component
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Counterparty: 'Counterparty',
  Side: 'Sides',
  Symbol: 'Symbols',
  User: 'Users',
  SubAccount: 'SubAccounts',
  MarketAccount: 'MarketAccounts',
  TradeStatus: 'Statuses',
  OrderID: 'OrderID',
  RFQID: 'RFQID',
  TradeID: 'TradeID',
} satisfies Partial<Record<keyof CustomerTrade, keyof CustomerTradeFilter>>;
export function colIDToFilterBuilderKey(id: string): keyof CustomerTradeFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}

type CustomerTradeServerFilter = Pick<
  CustomerTradeFilter,
  'StartDate' | 'EndDate' | 'Symbols' | 'OrderID' | 'RFQID' | 'TradeID'
> & {
  Counterparty?: string; // singular
};

export function onlyServerFilterKeys(filter?: CustomerTradeFilter) {
  if (filter == null) {
    return filter;
  }

  const serverFilter = {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
    Symbols: filter.Symbols,
    OrderID: filter.OrderID,
    RFQID: filter.RFQID,
    TradeID: filter.TradeID,
    Counterparty: filter.Counterparty?.at(0),
  } satisfies CustomerTradeServerFilter;

  return serverFilter;
}
